import React, { useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaBuilding,
  FaCheck,
  FaEdit,
  FaEnvelope,
  FaFileAlt,
  FaGlobe,
  FaMapMarkerAlt,
  FaPhone,
} from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import PortalLocationSelects from "./PortalLocationSelects";
import PortalAddressPicker from "./PortalAddressPicker";
import PortalAlert from "./PortalAlert";
import { stateShortName } from "../utils/stateCodes";
import {
  COMPANY_READONLY,
  COMPANY_DETAILS_READONLY,
  COMPANY_EDITABLE,
  COMPANY_ADDRESS_EDITABLE,
  displayCompanyValue,
} from "./companyProfileFields";

function buildCompanyForm(data) {
  const initial = {};
  [...COMPANY_EDITABLE, ...COMPANY_ADDRESS_EDITABLE].forEach(([key]) => {
    initial[key] = data?.[key] || "";
  });
  initial.countryId = data?.Country_Id ? String(data.Country_Id) : "";
  initial.stateId = data?.State_Id ? String(data.State_Id) : "";
  initial.cityId = data?.City_Id ? String(data.City_Id) : "";
  return initial;
}

function buildSavePayload(form) {
  const payload = {};
  [...COMPANY_EDITABLE, ...COMPANY_ADDRESS_EDITABLE].forEach(([key]) => {
    payload[key] = typeof form[key] === "string" ? form[key].trim() || null : form[key];
  });
  payload.Country_Id = form.countryId ? Number(form.countryId) : null;
  payload.State_Id = form.stateId ? Number(form.stateId) : null;
  payload.City_Id = form.cityId ? Number(form.cityId) : null;
  return payload;
}

function companyInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "C";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
}

function DetailItem({ label, value, children }) {
  return (
    <div className="cportal-profile-view-item">
      <span className="cportal-profile-view-label">{label}</span>
      <span className="cportal-profile-view-value">{children ?? displayCompanyValue(value)}</span>
    </div>
  );
}

function DetailSection({ icon: Icon, title, children, className = "", actions = null }) {
  return (
    <section className={`cportal-card cportal-detail-section ${className}`.trim()}>
      <div className="cportal-detail-section-head">
        <h2 className="cportal-detail-section-title">
          {Icon ? <Icon className="cportal-detail-section-icon" aria-hidden="true" /> : null}
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive") return "cportal-badge cportal-badge--danger";
  return "cportal-badge";
}

function formatAddressLine(company) {
  return [
    company?.Address_Line_1,
    company?.Address_Line_2,
    company?.City_Name,
    company?.State_Name,
    company?.Country_Name,
    company?.Zip_Code,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

function formatCityState(company) {
  return [company?.City_Name, company?.State_Name]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

function joinParts(parts, separator) {
  return parts
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(separator);
}

function formatPostalRows(company) {
  return [
    {
      label: "Address",
      value: joinParts([company?.Address_Line_1, company?.Address_Line_2], ", "),
    },
    {
      label: "City, State",
      value: joinParts([company?.City_Name, stateShortName(company?.State_Name)], ", "),
    },
    {
      label: "Country, Zip Code",
      value: joinParts(
        [company?.Country_Short_Code || company?.Country_Name, company?.Zip_Code],
        " "
      ),
    },
  ];
}

function googleMapsUrl(address) {
  const query = String(address || "").trim();
  if (!query) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function googleMapsEmbedUrl(address) {
  const query = String(address || "").trim();
  if (!query) return null;
  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`;
}

function TenantCompanyProfile() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [mapsOpen, setMapsOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/company");
        if (!active) return;
        const data = res.data?.data || {};
        setCompany(data);
        setForm(buildCompanyForm(data));
      } catch (_) {
        if (active) setError("Could not load company profile.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const openEdit = () => {
    setForm(buildCompanyForm(company));
    setMessage({ type: "", text: "" });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm(buildCompanyForm(company));
    setMessage({ type: "", text: "" });
  };

  const saveCompany = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    setSaving(true);
    try {
      const res = await axiosInstance.put("/client/company", buildSavePayload(form));
      const data = res.data?.data;
      if (data) {
        setCompany(data);
        setForm(buildCompanyForm(data));
      }
      setEditing(false);
      setMessage({ type: "success", text: "Company profile updated." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update company profile.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cportal-page">
        <p className="cportal-muted">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cportal-page">
        <PortalAlert type="error" message={error} onClear={() => setError("")} />
      </div>
    );
  }

  const phone = company?.Company_Phone;
  const email = company?.Company_Email;
  const website = company?.Website;
  const websiteHref =
    website && String(website).trim()
      ? /^https?:\/\//i.test(website)
        ? website
        : `https://${website}`
      : null;
  const companyName = displayCompanyValue(company?.Company_Name);
  const businessType = String(company?.Domain_Name || "").trim();
  const websiteDomain = String(company?.Website_Domain_Name || "").trim();
  const addressSummary = formatAddressLine(company);
  const cityState = formatCityState(company);
  const postalRows = formatPostalRows(company);
  const mapsUrl = googleMapsUrl(addressSummary);
  const mapsEmbedUrl = googleMapsEmbedUrl(addressSummary);

  const openMapsModal = () => {
    if (mapsEmbedUrl || mapsUrl) setMapsOpen(true);
  };

  const closeMapsModal = () => setMapsOpen(false);

  if (editing) {
    return (
      <div className="cportal-page">
        <button type="button" className="cportal-back-link" onClick={cancelEdit}>
          <FaArrowLeft /> Back to Company Profile
        </button>

        <form onSubmit={saveCompany}>
          <div className="cportal-entity-hero">
            <div className="cportal-entity-hero-left">
              <div className="cportal-entity-avatar cportal-entity-avatar--company" aria-hidden="true">
                {companyInitials(company?.Company_Name)}
              </div>
              <div className="cportal-entity-hero-meta">
                <h1 className="cportal-entity-hero-name">Edit Company Profile</h1>
                <div className="cportal-entity-hero-sub">
                  <span className={statusBadgeClass(company?.Status)}>
                    {displayCompanyValue(company?.Status)}
                  </span>
                  {businessType ? (
                    <span className="cportal-company-hero-hint" title="Business Type">
                      {businessType}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <PortalAlert
            type={message.type === "error" ? "error" : "success"}
            message={message.text}
            onClear={() => setMessage({ type: "", text: "" })}
          />

          <div className="cportal-edit-sections">
            <DetailSection icon={FaFileAlt} title="Company Details">
              <p className="cportal-section-help">
                Core company identity and website details for your Cloud Connect profile.
              </p>
              <div className="cportal-form-grid cportal-form-grid--3">
                {COMPANY_DETAILS_READONLY.map(([key, label]) => (
                  <div className="cportal-field" key={key}>
                    <label>{label}</label>
                    <input value={displayCompanyValue(company?.[key])} disabled />
                  </div>
                ))}
                {COMPANY_EDITABLE.map(([key, label]) => (
                  <div className="cportal-field" key={key}>
                    <label>{label}</label>
                    <input value={form[key] || ""} onChange={(e) => setField(key, e.target.value)} />
                  </div>
                ))}
                {COMPANY_READONLY.map(([key, label]) => (
                  <div className="cportal-field" key={key}>
                    <label>{label}</label>
                    <input value={displayCompanyValue(company?.[key])} disabled />
                  </div>
                ))}
              </div>
            </DetailSection>

            <DetailSection icon={FaMapMarkerAlt} title="Address">
              <p className="cportal-section-help">
                Search with Google Maps or select from the map, then fine-tune the fields below.
              </p>
              <PortalAddressPicker
                initialQuery={formatAddressLine({
                  ...company,
                  Address_Line_1: form.Address_Line_1,
                  Address_Line_2: form.Address_Line_2,
                  Zip_Code: form.Zip_Code,
                })}
                onResolved={(resolved) =>
                  setForm((f) => ({
                    ...f,
                    Address_Line_1: resolved.Address_Line_1 || f.Address_Line_1,
                    Address_Line_2: resolved.Address_Line_2 || f.Address_Line_2,
                    Zip_Code: resolved.Zip_Code || f.Zip_Code,
                    countryId: resolved.countryId || f.countryId,
                    stateId: resolved.stateId || "",
                    cityId: resolved.cityId || "",
                  }))
                }
              />
              <div className="cportal-address-fields">
                <div className="cportal-form-grid">
                  <div className="cportal-field">
                    <label>Address Line 1</label>
                    <input
                      value={form.Address_Line_1 || ""}
                      onChange={(e) => setField("Address_Line_1", e.target.value)}
                    />
                  </div>
                  <div className="cportal-field">
                    <label>Address Line 2</label>
                    <input
                      value={form.Address_Line_2 || ""}
                      onChange={(e) => setField("Address_Line_2", e.target.value)}
                    />
                  </div>
                </div>
                <div className="cportal-form-grid cportal-form-grid--3">
                  <PortalLocationSelects
                    countryId={form.countryId}
                    stateId={form.stateId}
                    cityId={form.cityId}
                    onChange={({ countryId, stateId, cityId }) =>
                      setForm((f) => ({ ...f, countryId, stateId, cityId }))
                    }
                  />
                  <div className="cportal-field">
                    <label>Zip Code</label>
                    <input
                      value={form.Zip_Code || ""}
                      onChange={(e) => setField("Zip_Code", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </DetailSection>
          </div>

          <div className="cportal-form-actions cportal-company-edit-actions">
            <button
              type="button"
              className="cportal-btn cportal-btn-ghost"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="cportal-btn cportal-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="cportal-page">
      {message.text && (
        <PortalAlert
          type={message.type === "error" ? "error" : "success"}
          message={message.text}
          onClear={() => setMessage({ type: "", text: "" })}
        />
      )}

      <div className="cportal-entity-hero cportal-company-hero">
        <div className="cportal-entity-hero-left">
          <div className="cportal-entity-avatar-wrap">
            <div className="cportal-entity-avatar cportal-entity-avatar--company" aria-hidden="true">
              {companyInitials(company?.Company_Name)}
            </div>
            {company?.Status ? (
              <span
                className={`cportal-avatar-status${
                  String(company.Status).toLowerCase() === "active" ? " is-active" : ""
                }`}
                role="img"
                aria-label={`Status: ${displayCompanyValue(company.Status)}`}
                title={displayCompanyValue(company.Status)}
              >
                {String(company.Status).toLowerCase() === "active" ? (
                  <FaCheck aria-hidden="true" />
                ) : null}
              </span>
            ) : null}
          </div>
          <div className="cportal-entity-hero-meta">
            <h1 className="cportal-entity-hero-name">{companyName}</h1>
            {cityState ? <p className="cportal-company-hero-address">{cityState}</p> : null}
            {businessType ? (
              <div className="cportal-entity-hero-sub">
                <span className="cportal-company-hero-hint" title="Business Type">
                  {businessType}
                </span>
              </div>
            ) : null}
          </div>
        </div>
        <div className="cportal-entity-hero-actions cportal-company-hero-actions">
          {mapsUrl || mapsEmbedUrl ? (
            <button
              type="button"
              className="cportal-btn cportal-btn-edit cportal-company-hero-icon-btn"
              onClick={openMapsModal}
              aria-label="Open address in Google Maps"
              title="Open in Google Maps"
            >
              <FaMapMarkerAlt aria-hidden="true" />
            </button>
          ) : null}
          <button
            type="button"
            className="cportal-btn cportal-btn-edit cportal-company-hero-icon-btn"
            onClick={openEdit}
            aria-label="Edit company profile"
            title="Edit company profile"
          >
            <FaEdit aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="cportal-company-contact-strip">
        <div className="cportal-company-contact-item">
          <span className="cportal-company-contact-icon" aria-hidden="true">
            <FaEnvelope />
          </span>
          <div className="cportal-company-contact-copy">
            <span className="cportal-company-contact-label">Company Email</span>
            {email ? (
              <a href={`mailto:${email}`} className="cportal-contact-link">
                {email}
              </a>
            ) : (
              <span className="cportal-muted">—</span>
            )}
          </div>
        </div>
        <div className="cportal-company-contact-item">
          <span className="cportal-company-contact-icon" aria-hidden="true">
            <FaPhone />
          </span>
          <div className="cportal-company-contact-copy">
            <span className="cportal-company-contact-label">Company Phone</span>
            {phone ? (
              <a href={`tel:${String(phone).replace(/[^\d+]/g, "")}`} className="cportal-contact-link">
                {phone}
              </a>
            ) : (
              <span className="cportal-muted">—</span>
            )}
          </div>
        </div>
        <div className="cportal-company-contact-item">
          <span className="cportal-company-contact-icon" aria-hidden="true">
            <FaGlobe />
          </span>
          <div className="cportal-company-contact-copy">
            <span className="cportal-company-contact-label">Website</span>
            {websiteHref ? (
              <a
                href={websiteHref}
                target="_blank"
                rel="noopener noreferrer"
                className="cportal-contact-link"
              >
                {website}
              </a>
            ) : (
              <span className="cportal-muted">—</span>
            )}
          </div>
        </div>
        <div className="cportal-company-contact-item">
          <span className="cportal-company-contact-icon" aria-hidden="true">
            <FaBuilding />
          </span>
          <div className="cportal-company-contact-copy">
            <span className="cportal-company-contact-label">Website Domain</span>
            <span>{displayCompanyValue(websiteDomain)}</span>
          </div>
        </div>
      </div>

      <DetailSection
        icon={FaMapMarkerAlt}
        title="Address"
        className="cportal-company-address-section"
        actions={
          mapsUrl || mapsEmbedUrl ? (
            <button
              type="button"
              className="cportal-company-maps-icon"
              onClick={openMapsModal}
              aria-label="Open address in Google Maps"
              title="Open in Google Maps"
            >
              <FaMapMarkerAlt aria-hidden="true" />
            </button>
          ) : null
        }
      >
        <div className="cportal-profile-view-grid">
          {postalRows.map(({ label, value }) => (
            <DetailItem key={label} label={label} value={value} />
          ))}
        </div>
      </DetailSection>

      {mapsOpen && (mapsEmbedUrl || mapsUrl) ? (
        <div className="cportal-modal-overlay" onClick={closeMapsModal} role="presentation">
          <div
            className="cportal-modal cportal-modal--wide cportal-company-maps-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="company-maps-title"
          >
            <div className="cportal-modal-header">
              <h3 id="company-maps-title">Company Location</h3>
              <button type="button" onClick={closeMapsModal} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="cportal-company-maps-modal-body">
              {addressSummary ? (
                <p className="cportal-company-maps-modal-address">{addressSummary}</p>
              ) : null}
              {mapsEmbedUrl ? (
                <div className="cportal-company-maps-frame-wrap">
                  <iframe
                    title="Company location on Google Maps"
                    src={mapsEmbedUrl}
                    className="cportal-company-maps-frame"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              ) : null}
              {mapsUrl ? (
                <div className="cportal-company-maps-modal-actions">
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cportal-btn cportal-btn-primary"
                  >
                    <FaMapMarkerAlt aria-hidden="true" />
                    Open in Google Maps
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default TenantCompanyProfile;
